/** Configuration > Tax and levies (NZ 2026/27). Informational + editable. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import SaveIndicator from "@/components/SaveIndicator";
import useAutosave from "@/hooks/useAutosave";

// Module-level so inputs keep focus (an inner component remounts on every keystroke).
function F({ label, children, hint }) {
  return (
    <label className="block text-sm">
      <span className="text-silver">{label}</span>
      {children}
      {hint && <div className="text-[11px] opacity-60 mt-0.5">{hint}</div>}
    </label>
  );
}

export default function TaxAndLevies() {
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/tax-settings").then(r => setT(r.data)); }, []);
  const autosave = useAutosave(async (_k, patch) => { await api.patch("/tax-settings", patch); });
  function updT(patch) { setT(prev => { const next = { ...prev, ...patch }; autosave.schedule("tax_settings", next); return next; }); }
  async function save() {
    await api.patch("/tax-settings", t);
    setMsg("Saved ✓");
    setTimeout(() => setMsg(""), 2500);
  }
  if (!t) return null;
  return (
    <div className="card space-y-3" data-testid="tax-and-levies">
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <h3 className="text-silver">Tax and levies <span className="opacity-60 text-xs">({t.label || "as at 2026/27, check yearly"})</span></h3>
        <div className="flex gap-2 items-center">
          <SaveIndicator status={autosave.status} data-testid="tax-save-indicator"/>
          <button className="btn-silver" onClick={save} data-testid="btn-tax-save">Save</button>
        </div>
      </div>
      {msg && <div className="text-green-400 text-sm">{msg}</div>}
      <p className="text-xs opacity-70">
        Informational. The Hub does <b>not</b> automatically deduct ACC earners' levy from contractors — contractors pay their own. These numbers power tooltips and the Payroll screen.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="ACC earners' levy % (2026/27)" hint="1.75% of liable income (includes GST)">
          <NumberInput className="input" value={t.acc_earners_levy_pct} onChange={e=>updT({acc_earners_levy_pct:parseFloat(e.target.value||0)})} data-testid="tx-acc-pct"/></F>
        <F label="ACC max liable earnings" hint="2026/27: $156,641">
          <NumberInput className="input" money value={t.acc_max_liable_earnings} onChange={e=>updT({acc_max_liable_earnings:parseFloat(e.target.value||0)})} data-testid="tx-acc-max-earn"/></F>
        <F label="ACC max levy" hint="2026/27: $2,741.22">
          <NumberInput className="input" money value={t.acc_max_levy} onChange={e=>updT({acc_max_levy:parseFloat(e.target.value||0)})} data-testid="tx-acc-max-levy"/></F>
        <F label="Next year note">
          <input className="input" value={t.acc_next_year_note||""} onChange={e=>updT({acc_next_year_note:e.target.value})}/></F>
        <F label="No-IR330C withholding %" hint="IRD no-notification rate — default 45%">
          <NumberInput className="input" value={t.no_ir330c_withholding_pct} onChange={e=>updT({no_ir330c_withholding_pct:parseFloat(e.target.value||0)})} data-testid="tx-no-ir330c"/></F>
        <F label="Non-resident company %" hint="20%">
          <NumberInput className="input" value={t.non_resident_company_withholding_pct} onChange={e=>updT({non_resident_company_withholding_pct:parseFloat(e.target.value||0)})}/></F>
        <F label="Minimum own schedular rate %" hint="At least 10% when a contractor chooses their own rate">
          <NumberInput className="input" value={t.minimum_own_schedular_rate_pct} onChange={e=>updT({minimum_own_schedular_rate_pct:parseFloat(e.target.value||0)})}/></F>
        <F label="GST %" hint="15% in NZ"><NumberInput className="input" value={t.gst_pct} onChange={e=>updT({gst_pct:parseFloat(e.target.value||0)})}/></F>
      </div>
    </div>
  );
}
